import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import { FaArrowLeft, FaUsers, FaTrash, FaEnvelope, FaSms } from "react-icons/fa";
import { useDispatch } from 'react-redux';
import { setActiveLink } from '../redux/event/event.slice';
import axios from 'axios';

const useStyles = makeStyles({
  container: {
    padding: "20px"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px"
  },
  backButton: {
    display: "flex",
    alignItems: "center",
    background: "none",
    border: "none",
    color: "#0d6efd",
    cursor: "pointer",
    padding: "5px 10px",
    fontSize: "15px",
    fontWeight: "bold"
  },
  eventInfo: {
    backgroundColor: "#f8f9fa",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "15px",
    marginBottom: "20px"
  },
  eventTitle: {
    fontSize: "22px",
    fontWeight: "bold",
    marginBottom: "10px"
  },
  eventDetail: {
    margin: "5px 0",
    color: "#555"
  },
  attendeeCount: {
    display: "inline-block",
    padding: "5px 15px",
    backgroundColor: "#e3f2fd",
    borderRadius: "20px",
    color: "#1976d2",
    fontWeight: "bold",
    marginBottom: "10px"
  },
  searchContainer: {
    marginBottom: "20px"
  },
  searchInput: {
    width: "100%",
    padding: "10px 15px",
    borderRadius: "4px",
    border: "1px solid #ced4da",
    fontSize: "16px"
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: "20px",
    backgroundColor: "#fff"
  },
  th: {
    backgroundColor: "#f8f9fa",
    border: "1px solid #dee2e6",
    padding: "12px 15px",
    textAlign: "left",
    fontWeight: "bold"
  },
  td: {
    border: "1px solid #dee2e6",
    padding: "12px 15px",
    verticalAlign: "middle"
  },
  actionButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    marginRight: "10px",
    fontSize: "16px",
    padding: "5px"
  },
  deleteButton: {
    color: "#dc3545"
  },
  bulkActionContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "20px"
  },
  bulkActionButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "10px 20px",
    borderRadius: "4px",
    border: "none",
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: "15px"
  },
  emailButton: {
    backgroundColor: "#0d6efd",
    color: "white",
    "&:hover": {
      backgroundColor: "#0b5ed7"
    }
  },
  smsButton: {
    backgroundColor: "#6c757d",
    color: "white",
    "&:hover": {
      backgroundColor: "#5c636a"
    }
  },
  modal: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000
  },
  modalContent: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "20px",
    width: "500px",
    maxWidth: "90%"
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "15px"
  },
  modalTitle: {
    fontSize: "20px",
    fontWeight: "bold"
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "22px",
    cursor: "pointer"
  },
  confirmButton: {
    backgroundColor: "#dc3545",
    color: "white",
    border: "none",
    padding: "8px 15px",
    borderRadius: "4px",
    cursor: "pointer",
    fontWeight: "bold",
    "&:hover": {
      backgroundColor: "#c82333"
    }
  },
  cancelButton: {
    backgroundColor: "#6c757d",
    color: "white",
    border: "none",
    padding: "8px 15px",
    borderRadius: "4px",
    marginRight: "10px",
    cursor: "pointer",
    fontWeight: "bold",
    "&:hover": {
      backgroundColor: "#5c636a"
    }
  },
  modalFooter: {
    marginTop: "20px",
    display: "flex",
    justifyContent: "flex-end"
  },
  formGroup: {
    marginBottom: "15px"
  },
  formLabel: {
    display: "block",
    marginBottom: "5px",
    fontWeight: "bold"
  },
  formControl: {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #ced4da",
    borderRadius: "4px",
    fontSize: "16px"
  },
  textArea: {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #ced4da",
    borderRadius: "4px",
    fontSize: "16px",
    minHeight: "150px",
    resize: "vertical"
  }
});

const EventAttendees = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Change from eventId to event-name to match route definition
  const { "event-name": eventName } = useParams();
  
  const [event, setEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [filteredAttendees, setFilteredAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [attendeeToDelete, setAttendeeToDelete] = useState(null);
  
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [messageData, setMessageData] = useState({
    subject: '',
    message: ''
  });
  const fetchEventData = useCallback(async () => {
    try {
      setLoading(true);
      // Use eventName instead of eventId
      console.log(`Fetching event data for: ${eventName}`);
      
      // Try with different endpoint formats to debug
      const endpoint = `https://api.thriftyx.com/admin/order/${eventName}`;
      console.log(`Using endpoint: ${endpoint}`);
      
      const response = await axios.get(endpoint);
      
      // Transform API data to match our component structure
      const attendeesList = response.data.data.map(order => ({
        id: order._id,
        name: order.user_id?.name || 'Unknown User',
        email: order.user_id?.email || 'No email',
        mobile: order.user_id?.phone || 'No phone',
        gender: order.user_id?.gender || 'Not specified',
        ticketType: order.tickets && order.tickets[0] ? order.tickets[0].name : 'Standard Ticket',
        status: order.status,
        orderId: order.order_id
      }));
      

      // Set event details from first order
      if (response.data.data.length > 0) {
        const firstOrder = response.data.data[0];
        
        setEvent({
          id: firstOrder.event_id.id,
          title: firstOrder.event_id.name ,
          date: firstOrder.event_id.date,
          location: firstOrder.event_id.venue_name,
          // description: cleanDescription // Remove HTML tags from description firstOrder.event_id.description.replace(/(<([^>]+)>)/gi, "")
        });
      } else {
        // Set default event details if no orders found
        setEvent({
          id: eventName,
          title: 'Event Details',
          date: new Date().toISOString(),
          location: 'Location not available',
          description: 'No additional information available'
        });
      }

      setAttendees(attendeesList);
      setFilteredAttendees(attendeesList);
      setLoading(false);
    } catch (err) {
      console.error("API Error:", err);
      setError("Failed to fetch event data: " + (err.response?.data?.message || err.message));
      setLoading(false);
    }
  }, [eventName]);

  useEffect(() => {
    dispatch(setActiveLink("Event Attendees"));
    fetchEventData();
  }, [dispatch, fetchEventData]);

  useEffect(() => {
    // Filter attendees based on search term
    if (searchTerm.trim() === '') {
      setFilteredAttendees(attendees);
    } else {
      const filtered = attendees.filter(attendee => 
        attendee.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        attendee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        attendee.mobile.includes(searchTerm)
      );
      setFilteredAttendees(filtered);
    }
  }, [searchTerm, attendees]);

  const handleBack = () => {
    navigate('/events');
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleDeleteClick = (attendee) => {
    setAttendeeToDelete(attendee);
    setShowDeleteModal(true);
  };
  const confirmDelete = () => {
    try {
      // Call API to cancel registration
      
      // Update local state
      const updatedAttendees = attendees.filter(a => a.id !== attendeeToDelete.id);
      setAttendees(updatedAttendees);
      setFilteredAttendees(updatedAttendees.filter(attendee => 
        attendee.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        attendee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        attendee.mobile.includes(searchTerm)
      ));
      
      setShowDeleteModal(false);
      setAttendeeToDelete(null);
      alert('Registration cancelled successfully!');
    } catch (error) {
      alert('Failed to cancel registration: ' + error.message);
    }
  };

  const openEmailModal = () => {
    setMessageData({
      subject: `Information about ${event?.title}`,
      message: ''
    });
    setShowEmailModal(true);
  };

  const openSmsModal = () => {
    setMessageData({
      subject: '',
      message: ''
    });
    setShowSmsModal(true);
  };

  const handleMessageDataChange = (e) => {
    const { name, value } = e.target;
    setMessageData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const sendEmail = async (e) => {
    e.preventDefault();
    try {
      const emails = filteredAttendees.map(attendee => attendee.email);
      await axios.post('https://api.thriftyx.com/admin/send-mail', {
        emails,
        subject: messageData.subject,
        message: messageData.message
      });
      
      setShowEmailModal(false);
      alert('Emails sent successfully!');
    } catch (error) {
      alert('Failed to send emails: ' + error.message);
    }
  };
  const sendSms = async (e) => {
    e.preventDefault();
    try {
      const phones = filteredAttendees.map(attendee => attendee.mobile);
      await axios.post('https://api.thriftyx.com/admin/send-sms', {
        phones,
        message: messageData.message
      });
      
      setShowSmsModal(false);
      alert('SMS messages sent successfully!');
    } catch (error) {
      alert('Failed to send SMS: ' + error.message);
    }
  };

  // Format date to a more readable format
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) return <div className="container"><div className="text-center mt-5"><div className="spinner-border" role="status"></div><p>Loading event details...</p></div></div>;
  if (error) return <div className="container"><div className="alert alert-danger mt-5" role="alert">{error}</div></div>;
  if (!event) return <div className="container"><div className="alert alert-warning mt-5" role="alert">Event not found.</div></div>;

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <button className={classes.backButton} onClick={handleBack}>
          <FaArrowLeft style={{ marginRight: "5px" }} /> Back to Events
        </button>
        <div className={classes.attendeeCount}>
          <FaUsers style={{ marginRight: "5px" }} />
          {filteredAttendees.length} Attendees
        </div>
      </div>
      
      <div className={classes.eventInfo}>        <h2 className={classes.eventTitle}>{event.title}</h2>
        <p className={classes.eventDetail}><strong>Date:</strong> {formatDate(event.date)}</p>
        <p className={classes.eventDetail}><strong>Location:</strong> {event.location}</p>
        {/* <p className={classes.eventDetail}><strong>Description:</strong> {event.description}</p> */}
      </div>
      
      <div className={classes.searchContainer}>
        <input 
          type="text" 
          placeholder="Search by name, email, or phone..." 
          className={classes.searchInput}
          value={searchTerm}
          onChange={handleSearch}
        />
      </div>
      
      <div className="table-responsive">
        <table className={classes.table}>
          <thead>
            <tr>              <th className={classes.th}>Name</th>
              <th className={classes.th}>Ticket Type</th>
              <th className={classes.th}>Mobile</th>
              <th className={classes.th}>Email</th>
              <th className={classes.th}>Gender</th>
              <th className={classes.th}>Status</th>
              <th className={classes.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAttendees.length > 0 ? (
              filteredAttendees.map(attendee => (
                <tr key={attendee.id}>                  <td className={classes.td}>{attendee.name}</td>
                  <td className={classes.td}>{attendee.ticketType}</td>
                  <td className={classes.td}>{attendee.mobile}</td>
                  <td className={classes.td}>{attendee.email}</td>
                  <td className={classes.td}>{attendee.gender}</td>
                  <td className={classes.td}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      backgroundColor: attendee.status === 'completed' ? '#d4edda' : '#fff3cd',
                      color: attendee.status === 'completed' ? '#155724' : '#856404'
                    }}>
                      {attendee.status}
                    </span>
                  </td>
                  <td className={classes.td}>
                    <button 
                      className={`${classes.actionButton} ${classes.deleteButton}`}
                      onClick={() => handleDeleteClick(attendee)}
                      title="Cancel Registration"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>              ))
            ) : (
              <tr>
                <td colSpan="7" className={classes.td} style={{ textAlign: "center" }}>
                  {searchTerm ? "No matching attendees found." : "No attendees registered for this event yet."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      <div className={classes.bulkActionContainer}>
        <button 
          className={`${classes.bulkActionButton} ${classes.emailButton}`}
          onClick={openEmailModal}
          disabled={filteredAttendees.length === 0}
        >
          <FaEnvelope /> Send Email to All
        </button>
        <button 
          className={`${classes.bulkActionButton} ${classes.smsButton}`}
          onClick={openSmsModal}
          disabled={filteredAttendees.length === 0}
        >
          <FaSms /> Send SMS to All
        </button>
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className={classes.modal}>
          <div className={classes.modalContent}>
            <div className={classes.modalHeader}>
              <h3 className={classes.modalTitle}>Confirm Cancellation</h3>
              <button className={classes.closeButton} onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <p>Are you sure you want to cancel the registration for {attendeeToDelete?.name}?</p>
            <p>This action cannot be undone.</p>
            <div className={classes.modalFooter}>
              <button className={classes.cancelButton} onClick={() => setShowDeleteModal(false)}>No, Keep</button>
              <button className={classes.confirmButton} onClick={confirmDelete}>Yes, Cancel Registration</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Email Modal */}
      {showEmailModal && (
        <div className={classes.modal}>
          <div className={classes.modalContent}>
            <div className={classes.modalHeader}>
              <h3 className={classes.modalTitle}>Send Email to All Attendees</h3>
              <button className={classes.closeButton} onClick={() => setShowEmailModal(false)}>×</button>
            </div>
            <form onSubmit={sendEmail}>
              <div className={classes.formGroup}>
                <label className={classes.formLabel}>Subject</label>
                <input
                  type="text"
                  className={classes.formControl}
                  name="subject"
                  value={messageData.subject}
                  onChange={handleMessageDataChange}
                  required
                />
              </div>
              <div className={classes.formGroup}>
                <label className={classes.formLabel}>Message</label>
                <textarea
                  className={classes.textArea}
                  name="message"
                  value={messageData.message}
                  onChange={handleMessageDataChange}
                  placeholder="Write your email message here..."
                  rows="6"
                  required
                ></textarea>
              </div>
              <div className={classes.modalFooter}>
                <button type="button" className={classes.cancelButton} onClick={() => setShowEmailModal(false)}>Cancel</button>
                <button type="submit" className={classes.emailButton} style={{ padding: "8px 15px", borderRadius: "4px" }}>Send Emails</button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* SMS Modal */}
      {showSmsModal && (
        <div className={classes.modal}>
          <div className={classes.modalContent}>
            <div className={classes.modalHeader}>
              <h3 className={classes.modalTitle}>Send SMS to All Attendees</h3>
              <button className={classes.closeButton} onClick={() => setShowSmsModal(false)}>×</button>
            </div>
            <form onSubmit={sendSms}>
              <div className={classes.formGroup}>
                <label className={classes.formLabel}>Message</label>
                <textarea
                  className={classes.textArea}
                  name="message"
                  value={messageData.message}
                  onChange={handleMessageDataChange}
                  placeholder="Write your SMS message here..."
                  rows="6"
                  required
                  maxLength={250}
                ></textarea>
                <small>{messageData.message.length}/250 characters</small>
              </div>
              <div className={classes.modalFooter}>
                <button type="button" className={classes.cancelButton} onClick={() => setShowSmsModal(false)}>Cancel</button>
                <button type="submit" className={classes.smsButton} style={{ padding: "8px 15px", borderRadius: "4px" }}>Send SMS</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventAttendees;